import React, { useState } from "react";
import styles from "../../../style/cssModules/table/Pagination.module.css"; // Import your CSS Module
import leftIcon from "../../../assets/images/icons/Left_MD.png";
import rightIcon from "../../../assets/images/icons/Right_MD.png";
import TimingFunctions from "../../../assets/functions/TimingFunctions";
import { ChevronLeft, ChevronRight } from "lucide-react";
import DropDownIcon from "../dropdownicon";
import { useTranslation } from "react-i18next";
import useRTLContext from "../../../hooks/useRTLContext";

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage,
  handleItemsPerPageChange,
  noAuditTrail = true,
}) => {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const [inputPage, setInputPage] = useState(currentPage);
  const { deBounce } = TimingFunctions();
  const [error, setError] = useState("");

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page); // Call the function passed from the parent to change the page
    }
  };

  // Function to get visible pages with ellipses
  const getVisiblePages = () => {
    const maxVisiblePages = 6; // Max number of pages to show at a time
    const visiblePages = [];

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages are 6 or less
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    // Add the first page always
    visiblePages.push(1);

    const halfRange = Math.floor((maxVisiblePages - 2) / 2); // Minus 2 to reserve space for first and last pages
    let start = Math.max(2, currentPage - halfRange);
    let end = Math.min(totalPages - 1, currentPage + halfRange);

    // Adjust if too close to the start or end
    if (currentPage <= halfRange + 2) {
      start = 2;
      end = Math.min(maxVisiblePages - 2, totalPages - 1);
    } else if (currentPage >= totalPages - halfRange - 1) {
      start = Math.max(totalPages - maxVisiblePages + 2, 2);
      end = totalPages - 1;
    }

    // Add ellipses if necessary
    if (start > 2) visiblePages.push("...");
    for (let i = start; i <= end; i++) {
      visiblePages.push(i);
    }
    if (end < totalPages - 1) visiblePages.push("...");

    // Add the last page always
    visiblePages.push(totalPages);

    return visiblePages;
  };

  const searchPagination = (targetPage) => {
    setInputPage(targetPage);
    deBounce(() => handleInputChange(targetPage), 1000);
  };
  const handleInputChange = async (targetPage) => {
    setInputPage(targetPage);
    // Remove leading zeros using regex
    const normalizedPageNumber = targetPage.replace(/^0+/, "") || "0";
    if (targetPage >= 1 && targetPage <= totalPages) {
      onPageChange(normalizedPageNumber);
      setError("");
    } else if (targetPage > totalPages) {
      setError("Page number exceeds available pages");
    } else if (targetPage && targetPage < 1) {
      console.log("targetpage ", targetPage);
      setError("Invalid Number");
    } else if (targetPage === "") {
      setError("");
      setInputPage("");
    }
  };

  const visiblePages = getVisiblePages();

  return (
    <div className={styles.paginationWrapper}>
      <div className={styles.leftHelper}>
        <div
          className={`${styles.navContainer} ${
            currentPage === 1 ? styles.disabled : ""
          }`}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          <ChevronLeft size={15} />
          <span className={styles.previousTxt}>{t("Previous")}</span>
        </div>
      </div>
      <div className={styles.paginationContainer}>
        {/* <div
          className={`${styles.numberBox} ${
            currentPage === 1 ? styles.disabled : ""
          }`}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          <img src={leftIcon} alt="left icon" />
        </div> */}

        {/* Render the page numbers dynamically with ellipses */}
        {visiblePages.map((page, index) => (
          <div
            key={index}
            className={`${styles.numberBox} ${
              String(currentPage) === String(page) ? styles.active : ""
            } ${typeof page === "string" ? styles.disabled : ""}`}
            onClick={() => typeof page === "number" && handlePageChange(page)}
          >
            <span
              className={`${styles.numberText} ${
                String(currentPage) === String(page) ? styles.activeNumber : ""
              }`}
            >
              {page}
            </span>
          </div>
        ))}
      </div>
      <div className={styles.rightWrapper}>
        <div className={styles.pageInputContainer}>
          {itemsPerPage && (
            <div className="relative">
              <select
                onChange={(e) => handleItemsPerPageChange(e.target.value)}
                value={itemsPerPage}
                className="appearance-none w-full bg-[#F9FAFB] text-[#04080B] border border-[#F2F4F7] rounded-lg py-2 pl-4 pr-10 focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
                <option value="40">40</option>
                <option value="50">50</option>
                <option value="500">500</option>
                {noAuditTrail && <option value="100">100</option>}
                {noAuditTrail && <option value="500">500</option>}
              </select>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#F9FAFB] pointer-events-none">
                <DropDownIcon />
              </span>
            </div>
          )}
          <div
            className={`${styles.navContainer} ${
              currentPage === totalPages ? styles.disabled : ""
            }`}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            <span className={styles.nextTxt}>{t("Next")}</span>
            <ChevronRight className="font-bold" size={15} />
          </div>
        </div>
        {error && <p className={styles.paginationError}>{error}</p>}
      </div>
    </div>
  );
};

export default Pagination;
